import { useEffect, useState } from "react";
import { http } from "../api";
import { PageHeader, EmptyState } from "../shared";
import { Users as UsersIcon, Plus, Trash2, ShieldCheck, X } from "lucide-react";

export default function BoothOwners({ user }) {
  const isSuper = user?.role === "Super Admin";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ booth: "", ward: 7, owner_user_ids: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [b, u] = await Promise.all([http.get("/booths/owners"), http.get("/users")]);
    setRows(b.data.rows || []);
    setUsers(u.data || []);
  };
  useEffect(() => { if (isSuper) load(); }, [isSuper]);

  const toggleUser = (uid) => {
    setForm((f) => ({ ...f, owner_user_ids: f.owner_user_ids.includes(uid) ? f.owner_user_ids.filter((x) => x !== uid) : [...f.owner_user_ids, uid] }));
  };

  const save = async () => {
    if (!form.booth.trim()) { window.alert("Booth number is required"); return; }
    setBusy(true);
    try {
      await http.post("/booths/owners", { ...form, ward: Number(form.ward) });
      setShowAdd(false); setForm({ booth: "", ward: 7, owner_user_ids: [] });
      load();
    } catch (e) { window.alert(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const editRow = (r) => { setForm({ booth: r.booth, ward: r.ward, owner_user_ids: r.owner_user_ids || [] }); setShowAdd(true); };
  const removeRow = async (r) => {
    if (!window.confirm(`Remove owners for Booth ${r.booth}?`)) return;
    await http.delete(`/booths/owners/${r.booth}?ward=${r.ward}`);
    load();
  };

  if (!isSuper) {
    return (
      <>
        <PageHeader eyebrow="BOOTH RESPONSIBLE" title="Booth responsible members" subtitle="Super Admin only" />
        <EmptyState icon={ShieldCheck} title="Super Admin access only" hint="Ask a Super Admin to assign booth-responsible members." />
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="GOVERNANCE" title="Booth responsible members"
        subtitle={`Assign one or more admin/members to each booth. They get an in-app alert + SMS + WhatsApp the moment a complaint is lodged. Unacknowledged after 30 min → auto-escalates to every Super Admin (also re-notifies the booth-responsible members as a final reminder).`}
        actions={<button className="primary" data-testid="bo-add-open" onClick={() => { setForm({ booth: "", ward: 7, owner_user_ids: [] }); setShowAdd(true); }}><Plus size={14} /> Assign booth</button>} />

      <section className="panel">
        {rows.length === 0 ? (
          <EmptyState icon={UsersIcon} title="No booth-responsible members assigned yet" hint="Click 'Assign booth' to make sure complaints route to the right people and escalate on time." />
        ) : (
          <ul className="bo-list" data-testid="bo-list">
            {rows.map((r) => (
              <li key={`${r.ward}-${r.booth}`} className="bo-row" data-testid={`bo-row-${r.booth}`}>
                <div className="bo-body">
                  <b>Ward {r.ward} · Booth {r.booth}</b>
                  <div className="bo-chips">
                    {(r.owners || []).length === 0 ? (
                      <em style={{ color: "#b04a3f" }}>No responsible member assigned</em>
                    ) : (r.owners || []).map((o) => (
                      <span key={o.user_id} className="bo-chip" data-testid={`bo-owner-${o.user_id}`}>
                        <b>{o.name}</b> <small>{o.role}</small>
                      </span>
                    ))}
                  </div>
                  <small>Updated {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"} by {r.updated_by_name || "—"}</small>
                </div>
                <div className="bo-actions">
                  <button className="outline" data-testid={`bo-edit-${r.booth}`} onClick={() => editRow(r)}>Edit</button>
                  <button className="danger" data-testid={`bo-remove-${r.booth}`} onClick={() => removeRow(r)}><Trash2 size={12} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showAdd && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowAdd(false); }}>
          <div className="bo-drawer" data-testid="bo-drawer">
            <div className="bo-drawer-head">
              <b>Assign booth-responsible members</b>
              <button className="icon-btn" data-testid="bo-drawer-close" onClick={() => setShowAdd(false)}><X size={14} /></button>
            </div>
            <div className="bo-drawer-grid">
              <label>Booth number *
                <input data-testid="bo-booth" value={form.booth} onChange={(e) => setForm({ ...form, booth: e.target.value })} placeholder="42" />
              </label>
              <label>Ward
                <input data-testid="bo-ward" type="number" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} />
              </label>
            </div>
            <small>Pick the members responsible for this booth ({form.owner_user_ids.length} selected):</small>
            <div className="bo-user-grid" data-testid="bo-user-grid">
              {users.map((u) => (
                <label key={u.user_id} className={`bo-user-card ${form.owner_user_ids.includes(u.user_id) ? "on" : ""}`} data-testid={`bo-user-${u.user_id}`}>
                  <input type="checkbox" checked={form.owner_user_ids.includes(u.user_id)} onChange={() => toggleUser(u.user_id)} />
                  <div>
                    <b>{u.name}</b>
                    <small>{u.role} · {u.mobile || "no mobile"}</small>
                  </div>
                </label>
              ))}
            </div>
            <div className="bo-drawer-actions">
              <button className="outline" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="primary" data-testid="bo-save" disabled={busy || !form.booth.trim()} onClick={save}>{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
